import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Form, InputGroup, Modal, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { toast } from "react-toastify";
import moment from "moment";
import apiCall from "./../../API/API";
import ExportButtons, { collectPages } from "../../Component/ExportButtons/ExportButtons";
import "../PlanSettings/PlanSettings.css";
import "./ActivationRequests.css";
import "../SubAdmins/SubAdmins.css";
import { useAdminAccess, loadAdminAccess } from "../../Config/adminAccess";

const TABS = [
  { key: "0", title: "Pending" },
  { key: "1", title: "Approved" },
  { key: "2", title: "Rejected" },
];
const PAGE_SIZES = [10, 20, 50, 100];
const EMPTY_FILTERS = { search: "", packageId: "", from: "", to: "", min_amount: "", max_amount: "", source: "", sort: "" };
const SORT_OPTIONS = [
  { value: "", label: "Default order" },
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "amount_desc", label: "Amount: high to low" },
  { value: "amount_asc", label: "Amount: low to high" },
];
const OPEN_ENDED_MAX = 100000000;

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmt = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "");
const today = () => moment().format("YYYY-MM-DD");
const initials = (r) =>
  String(r?.name || r?.username || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
const rangeText = (p) =>
  p.max_amount >= OPEN_ENDED_MAX ? `${inr(p.min_amount)} onwards` : p.min_amount === p.max_amount ? inr(p.min_amount) : `${inr(p.min_amount)} – ${inr(p.max_amount)}`;

const BackDateNote = ({ date }) =>
  date && date !== today() ? (
    <div className="ps-note ps-note-warn">
      Back-dated to <strong>{moment(date).format("DD MMM YYYY")}</strong>. Growth is paid at the month-end closing from{" "}
      <strong>{moment.max(moment(date), moment().startOf("month")).format("DD MMM YYYY")}</strong>: earlier months are not paid.
      Direct income is paid now.
    </div>
  ) : null;

/** Admin activates a package for any member directly (no member request needed). */
const DirectActivateModal = ({ show, packages, onClose, onDone }) => {
  const [username, setUsername] = useState("");
  const [member, setMember] = useState({ state: "idle", name: "" });
  const [packageId, setPackageId] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setUsername("");
    setMember({ state: "idle", name: "" });
    setPackageId("");
    setAmount("");
    setDate(today());
    setNote("");
  }, [show]);

  // Look the member up while typing.
  useEffect(() => {
    const u = username.trim();
    if (!u) return setMember({ state: "idle", name: "" });
    setMember({ state: "checking", name: "" });
    const t = setTimeout(async () => {
      const { data, error } = await apiCall("post", "/check_username", { username: u });
      if (data?.name) setMember({ state: "found", name: data.name });
      else setMember({ state: error ? "missing" : "found", name: data?.name || "" });
    }, 450);
    return () => clearTimeout(t);
  }, [username]);

  const pkg = useMemo(() => packages.find((p) => String(p.packageId) === String(packageId)), [packages, packageId]);

  const pickPackage = (id) => {
    setPackageId(id);
    const p = packages.find((x) => String(x.packageId) === String(id));
    if (p) setAmount(String(p.min_amount));
  };

  const amountError = (() => {
    if (!pkg || amount === "") return "";
    const a = Number(amount);
    if (!Number.isFinite(a) || a < pkg.min_amount) return `Minimum ${inr(pkg.min_amount)}`;
    if (a > pkg.max_amount) return `Maximum ${inr(pkg.max_amount)}`;
    return "";
  })();

  const canSubmit = member.state === "found" && pkg && amount !== "" && !amountError && date && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    const { data, error } = await apiCall("POST", "/activation-requests/direct", {
      username: username.trim(),
      packageId: pkg.packageId,
      amount: Number(amount),
      activation_date: date,
      note: note.trim(),
    });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not activate.");
    toast.success(data?.message || "Package activated.");
    onDone();
  };

  return (
    <Modal show={show} onHide={onClose} centered size="lg" className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Activate Package</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="ps-form-grid">
            <Form.Group className="ps-span-2">
              <Form.Label>Member username</Form.Label>
              <Form.Control value={username} onChange={(e) => setUsername(e.target.value)} placeholder="e.g. PB496512" autoFocus required />
              <Form.Text className={member.state === "missing" ? "ps-error-text" : ""}>
                {member.state === "checking" && "Checking…"}
                {member.state === "found" && <>Member: <strong>{member.name || username}</strong></>}
                {member.state === "missing" && "No member with this username."}
              </Form.Text>
            </Form.Group>

            <Form.Group className="ps-span-2">
              <Form.Label>Package</Form.Label>
              <Form.Select value={packageId} onChange={(e) => pickPackage(e.target.value)} required>
                <option value="">Select a package</option>
                {packages.map((p) => (
                  <option key={p.packageId} value={p.packageId}>
                    {p.name} · {rangeText(p)} · {p.roi_percent}% {p.roi_frequency.toLowerCase()}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group>
              <Form.Label>Amount</Form.Label>
              <InputGroup>
                <InputGroup.Text>₹</InputGroup.Text>
                <Form.Control
                  type="number"
                  min={pkg?.min_amount}
                  max={pkg?.max_amount}
                  step="any"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  disabled={!pkg}
                  required
                />
              </InputGroup>
              <Form.Text className={amountError ? "ps-error-text" : ""}>
                {amountError || (pkg ? rangeText(pkg) : "Pick a package first")}
              </Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Label>Activation date</Form.Label>
              <Form.Control type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} required />
              <Form.Text>Today, or an earlier date to back-date it.</Form.Text>
            </Form.Group>

            <Form.Group className="ps-span-2">
              <Form.Label>Note (optional)</Form.Label>
              <Form.Control value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="e.g. paid by NEFT, ref 1234" />
            </Form.Group>
          </div>

          {pkg && member.state === "found" && amount !== "" && !amountError && (
            <div className="ar-summary">
              Activate <strong>{pkg.name}</strong> with <strong>{inr(amount)}</strong> for{" "}
              <strong>{member.name || username}</strong> from <strong>{moment(date).format("DD MMM YYYY")}</strong>.
            </div>
          )}
          <BackDateNote date={date} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="success" disabled={!canSubmit}>{saving ? <Spinner size="sm" /> : "Activate Now"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const accountText = (p) =>
  !p?.account
    ? "—"
    : p.method === "upi"
    ? p.account.upiId
    : `${p.account.bankName || "Bank"} · ${String(p.account.accountNumber || "").slice(-4)}`;

/** The member's payment for a request: where they paid, the UTR and the screenshot. */
const PaymentDetails = ({ payment }) => {
  if (!payment) {
    return <div className="ps-note ps-note-warn">No payment details were submitted with this request.</div>;
  }
  const a = payment.account || {};
  return (
    <div className="ar-payment">
      <div className="ar-payment-info">
        <div className="ar-payment-row"><span>Paid by</span><strong>{payment.method === "upi" ? "UPI" : "Bank transfer"}</strong></div>
        {payment.method === "upi" ? (
          <div className="ar-payment-row"><span>Paid to</span><strong>{a.upiId || "—"}{a.name ? ` (${a.name})` : ""}</strong></div>
        ) : (
          <>
            <div className="ar-payment-row"><span>Bank</span><strong>{a.bankName || "—"}</strong></div>
            <div className="ar-payment-row"><span>Account</span><strong>{a.accountNumber || "—"}</strong></div>
            <div className="ar-payment-row"><span>IFSC</span><strong>{a.ifsc || "—"}</strong></div>
          </>
        )}
        <div className="ar-payment-row"><span>UTR / Txn no.</span><strong className="ar-utr">{payment.utr}</strong></div>
      </div>
      {payment.proof_url && (
        <a className="ar-proof" href={payment.proof_url} target="_blank" rel="noopener noreferrer" title="Open full screenshot">
          <img src={payment.proof_url} alt="Payment screenshot" />
          <span>Open screenshot</span>
        </a>
      )}
    </div>
  );
};

const ProofModal = ({ request, onClose }) => (
  <Modal show={!!request} onHide={onClose} centered size="lg" className="plan-settings-modal">
    <Modal.Header closeButton>
      <Modal.Title>Payment for request #{request?.request_Id}</Modal.Title>
    </Modal.Header>
    <Modal.Body>
      {request && (
        <>
          <p className="mb-3">
            {request.package_name} · <strong>{inr(request.amount)}</strong> by <strong>{request.name || request.username}</strong> ({request.username})
          </p>
          <PaymentDetails payment={request.payment} />
        </>
      )}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="outline-secondary" onClick={onClose}>Close</Button>
    </Modal.Footer>
  </Modal>
);

const ApproveModal = ({ request, onClose, onDone }) => {
  const [date, setDate] = useState(today());
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (request) setDate(today()); }, [request]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { data, error } = await apiCall("POST", "/activation-requests/approve", {
      request_Id: request.request_Id,
      activation_date: date,
    });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not approve.");
    toast.success(data?.message || "Approved.");
    onDone();
  };

  return (
    <Modal show={!!request} onHide={onClose} centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Approve request #{request?.request_Id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {request && (
            <>
              <div className="ar-member-card">
                <span className="ar-avatar">{initials(request)}</span>
                <div>
                  <div className="ps-strong">{request.name || request.username}</div>
                  <div className="ps-muted ps-small">{request.username}</div>
                </div>
                <div className="ar-member-amount">
                  <div className="ps-strong">{inr(request.amount)}</div>
                  <div className="ps-muted ps-small">{request.package_name}</div>
                </div>
              </div>
              {request.note && <div className="ps-note ps-note-info">Member's note: {request.note}</div>}
              <div className="mt-3"><PaymentDetails payment={request.payment} /></div>
              <Form.Group className="mt-3">
                <Form.Label>Activation date</Form.Label>
                <Form.Control type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} required />
                <Form.Text>Today by default. Pick an earlier date to activate it back-dated.</Form.Text>
              </Form.Group>
              <BackDateNote date={date} />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="success" disabled={saving}>{saving ? <Spinner size="sm" /> : "Approve & Activate"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const RejectModal = ({ request, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (request) setReason(""); }, [request]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const { data, error } = await apiCall("POST", "/activation-requests/reject", {
      request_Id: request.request_Id,
      reason: reason.trim(),
    });
    setSaving(false);
    if (error) return toast.error(error.message || "Could not reject.");
    toast.success(data?.message || "Rejected.");
    onDone();
  };

  return (
    <Modal show={!!request} onHide={onClose} centered className="plan-settings-modal">
      <Form onSubmit={submit}>
        <Modal.Header closeButton>
          <Modal.Title>Reject request #{request?.request_Id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {request && (
            <>
              <p className="mb-3">
                {request.package_name} · {inr(request.amount)} for <strong>{request.name || request.username}</strong> ({request.username})
              </p>
              <div className="mb-3"><PaymentDetails payment={request.payment} /></div>
            </>
          )}
          <Form.Group>
            <Form.Label>Reason (shown to the member)</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={300}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Payment not received"
              required
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" variant="danger" disabled={saving || !reason.trim()}>{saving ? <Spinner size="sm" /> : "Reject"}</Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

/** Page numbers with ellipses: 1 … 4 5 6 … 12 */
const pageList = (page, pages) => {
  const set = new Set([1, pages, page - 1, page, page + 1].filter((p) => p >= 1 && p <= pages));
  const sorted = [...set].sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push("…" + p);
    out.push(p);
  });
  return out;
};

const Pagination = ({ page, pages, total, limit, onPage, onLimit }) => {
  if (total === 0) return null;
  const first = (page - 1) * limit + 1;
  const last = Math.min(page * limit, total);
  return (
    <div className="ar-pagination">
      <div className="ar-pagination-info">
        Showing <strong>{first}–{last}</strong> of <strong>{total}</strong>
        <Form.Select size="sm" value={limit} onChange={(e) => onLimit(Number(e.target.value))} className="ar-page-size">
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} / page</option>)}
        </Form.Select>
      </div>
      <div className="ar-pages">
        <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => onPage(1)} title="First page">«</Button>
        <Button size="sm" variant="outline-secondary" disabled={page <= 1} onClick={() => onPage(page - 1)}>Prev</Button>
        {pageList(page, pages).map((p) =>
          typeof p === "string" ? (
            <span key={p} className="ar-ellipsis">…</span>
          ) : (
            <Button key={p} size="sm" variant={p === page ? "primary" : "outline-secondary"} onClick={() => onPage(p)}>{p}</Button>
          )
        )}
        <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => onPage(page + 1)}>Next</Button>
        <Button size="sm" variant="outline-secondary" disabled={page >= pages} onClick={() => onPage(pages)} title="Last page">»</Button>
      </div>
    </div>
  );
};

const ActivationRequests = () => {
  const [tab, setTab] = useState("0");
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ pending: 0, approved: 0, rejected: 0, pending_amount: 0 });
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [viewingProof, setViewingProof] = useState(null);
  const [showDirect, setShowDirect] = useState(false);
  const [packages, setPackages] = useState([]);

  const [access, setAccess] = useState(null);
  const loadedAccess = useAdminAccess();
  useEffect(() => { if (loadedAccess) setAccess(loadedAccess); }, [loadedAccess]);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ status: tab, page: String(page), limit: String(limit) });
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params.set(k, v); });
    const { data, error } = await apiCall("GET", `/activation-requests?${params.toString()}`);
    setLoading(false);
    if (error) return toast.error(error.message || "Could not load activation requests.");
    setRows(data?.data || []);
    setTotal(data?.total || 0);
    setPages(data?.pages || 1);
    if (data?.summary) setSummary(data.summary);
  }, [tab, page, limit, filters]);

  // Search applies after a short pause in typing; other filters apply at once.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === search.trim() ? f : { ...f, search: search.trim() }));
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const setFilter = (key) => (e) => {
    setFilters((f) => ({ ...f, [key]: e.target.value }));
    setPage(1);
  };
  const resetFilters = () => {
    setFilters(EMPTY_FILTERS);
    setSearch("");
    setPage(1);
  };
  const activeFilters = Object.entries(filters).filter(([k, v]) => v !== "" && k !== "sort").length;

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      const { data } = await apiCall("GET", "/plan-settings");
      setPackages((data?.data?.packages || []).filter((p) => p.status === 1));
    })();
  }, []);

  const pending = tab === "0";
  const changed = () => {
    load();
    window.dispatchEvent(new Event("activation-requests-changed"));
    if (access?.role === "manager") loadAdminAccess(true).then((a) => a && setAccess(a));
  };

  const dateLabel = tab === "1" ? "Activated" : "Requested";
  const tabTitle = TABS.find((t) => t.key === tab)?.title || "";
  const exportColumns = [
    { header: "Request #", value: (r) => r.request_Id },
    { header: "Requested on", value: (r) => fmt(r.requested_at) },
    { header: "Name", value: (r) => r.name },
    { header: "Username", value: (r) => r.username },
    { header: "Package", value: (r) => r.package_name },
    { header: "Amount", value: (r) => inr(r.amount) },
    { header: "Paid by", value: (r) => (r.direct ? "Direct by admin" : r.payment?.method === "upi" ? "UPI" : r.payment ? "Bank transfer" : "") },
    { header: "UTR / Txn no.", value: (r) => r.payment?.utr },
    { header: "Status", value: (r) => ["Pending", "Approved", "Rejected"][Number(r.status)] || "" },
    { header: "Activated from", value: (r) => (Number(r.status) === 1 ? fmt(r.activation_date) : "") },
    { header: "Reason", value: (r) => r.reject_reason },
    { header: "Reviewed by", value: (r) => r.reviewed_by },
    { header: "Reviewed on", value: (r) => fmt(r.reviewed_at) },
    { header: "Note", value: (r) => r.note },
  ];
  const exportRows = () =>
    collectPages(async (p, l) => {
      const params = new URLSearchParams({ status: tab, page: String(p), limit: String(l) });
      Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params.set(k, v); });
      const { data } = await apiCall("GET", `/activation-requests?${params.toString()}`);
      return { rows: data?.data || [], pages: data?.pages || 1 };
    });
  const exportFilters = Object.entries(filters).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}: ${v}`).join(", ");

  return (
    <div className="plan-settings activation-requests-page">
      <div className="ps-header">
        <div>
          <h2>Activation Requests</h2>
          <p>Approve or reject members' package requests, or activate a package for any member directly. Back dates are allowed.</p>
        </div>
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <ExportButtons title={`Activation Requests - ${tabTitle}`} columns={exportColumns} getRows={exportRows} filters={exportFilters} />
          <Button className="ar-primary-btn" onClick={() => setShowDirect(true)}>+ Activate Package</Button>
        </div>
      </div>

      {access && access.role === "manager" && (
        <div className="ar-limit">
          <span>
            Your approval limit for <strong>{access.month}</strong>: <strong>{inr(access.monthly_limit)}</strong>
          </span>
          <span>
            Used <strong>{inr(access.used_this_month)}</strong> · Left <strong>{inr(access.remaining_this_month)}</strong>
          </span>
          <span className="ps-muted ps-small">Requests above what is left must be approved by the admin.</span>
        </div>
      )}

      <div className="ps-stats ar-stats">
        <button type="button" className={`ps-stat ar-stat ar-stat--pending${tab === "0" ? " ar-stat--active" : ""}`} onClick={() => { setTab("0"); setPage(1); }}>
          <span>Pending</span>
          <strong>{summary.pending}</strong>
          <small>{inr(summary.pending_amount)} waiting</small>
        </button>
        <button type="button" className={`ps-stat ar-stat ar-stat--approved${tab === "1" ? " ar-stat--active" : ""}`} onClick={() => { setTab("1"); setPage(1); }}>
          <span>Approved</span>
          <strong>{summary.approved}</strong>
          <small>Packages activated</small>
        </button>
        <button type="button" className={`ps-stat ar-stat ar-stat--rejected${tab === "2" ? " ar-stat--active" : ""}`} onClick={() => { setTab("2"); setPage(1); }}>
          <span>Rejected</span>
          <strong>{summary.rejected}</strong>
          <small>With a reason sent</small>
        </button>
      </div>

      <Tabs activeKey={tab} onSelect={(k) => { setTab(k); setPage(1); }} className="ps-tabs">
        {TABS.map((t) => (
          <Tab key={t.key} eventKey={t.key} title={t.title}>
            <div className="ps-panel">
              <div className="ar-panel-title">
                <h3>{t.title} requests</h3>
                <span className="ar-count">{total}</span>
                {activeFilters > 0 && <span className="ar-filter-badge">{activeFilters} filter{activeFilters > 1 ? "s" : ""} on</span>}
              </div>

              <div className="ar-filters">
                <Form.Group className="ar-f-search">
                  <Form.Label>Search</Form.Label>
                  <Form.Control size="sm" placeholder="Username, name, UTR or #request" value={search} onChange={(e) => setSearch(e.target.value)} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Package</Form.Label>
                  <Form.Select size="sm" value={filters.packageId} onChange={setFilter("packageId")}>
                    <option value="">All packages</option>
                    {packages.map((p) => <option key={p.packageId} value={p.packageId}>{p.name}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group>
                  <Form.Label>{dateLabel} from</Form.Label>
                  <Form.Control size="sm" type="date" value={filters.from} max={filters.to || undefined} onChange={setFilter("from")} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>{dateLabel} to</Form.Label>
                  <Form.Control size="sm" type="date" value={filters.to} min={filters.from || undefined} onChange={setFilter("to")} />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Amount (₹)</Form.Label>
                  <div className="ar-range">
                    <Form.Control size="sm" type="number" min="0" placeholder="Min" value={filters.min_amount} onChange={setFilter("min_amount")} />
                    <span>–</span>
                    <Form.Control size="sm" type="number" min="0" placeholder="Max" value={filters.max_amount} onChange={setFilter("max_amount")} />
                  </div>
                </Form.Group>
                {tab === "1" && (
                  <Form.Group>
                    <Form.Label>Source</Form.Label>
                    <Form.Select size="sm" value={filters.source} onChange={setFilter("source")}>
                      <option value="">All</option>
                      <option value="member">Member request</option>
                      <option value="direct">Direct by admin</option>
                    </Form.Select>
                  </Form.Group>
                )}
                <Form.Group>
                  <Form.Label>Sort</Form.Label>
                  <Form.Select size="sm" value={filters.sort} onChange={setFilter("sort")}>
                    {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </Form.Select>
                </Form.Group>
                <div className="ar-f-reset">
                  <Button size="sm" variant="outline-secondary" onClick={resetFilters} disabled={activeFilters === 0 && !filters.sort}>Reset</Button>
                </div>
              </div>

              <div className="ps-table-wrap">
                <Table className="ps-table">
                  <thead>
                    <tr>
                      <th>Request</th>
                      <th>Member</th>
                      <th>Package</th>
                      <th>Amount</th>
                      <th>Payment</th>
                      {pending ? <th>Note</th> : <th>{t.key === "1" ? "Activated from" : "Reason"}</th>}
                      {!pending && <th>Reviewed by</th>}
                      {pending && <th className="text-end">Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {loading && (
                      <tr><td colSpan={8} className="ps-empty"><Spinner size="sm" /> Loading…</td></tr>
                    )}
                    {!loading && rows.length === 0 && (
                      <tr><td colSpan={8} className="ps-empty">{activeFilters > 0 ? "No requests match these filters." : pending ? "No pending requests. You're all caught up." : `No ${t.title.toLowerCase()} requests.`}</td></tr>
                    )}
                    {!loading && rows.map((r) => (
                      <tr key={r.request_Id}>
                        <td>
                          <div className="ps-strong">#{r.request_Id}</div>
                          <div className="ps-muted ps-small">{moment(r.requested_at).format("DD MMM YYYY, HH:mm")}</div>
                        </td>
                        <td>
                          <div className="ar-member">
                            <span className="ar-avatar">{initials(r)}</span>
                            <div>
                              <div className="ps-strong">{r.name || "—"}</div>
                              <div className="ps-muted ps-small">{r.username}</div>
                            </div>
                          </div>
                        </td>
                        <td>{r.package_name}</td>
                        <td className="ps-strong">{inr(r.amount)}</td>
                        <td>
                          {r.payment ? (
                            <button type="button" className="ar-payment-cell" onClick={() => setViewingProof(r)} title="View payment proof">
                              {r.payment.proof_url && <img src={r.payment.proof_url} alt="" />}
                              <span>
                                <span className="ar-utr">{r.payment.utr}</span>
                                <span className="ps-muted ps-small">{r.payment.method === "upi" ? "UPI" : "Bank"} · {accountText(r.payment)}</span>
                              </span>
                            </button>
                          ) : (
                            <span className="ps-muted ps-small">{r.direct ? "Direct by admin" : "—"}</span>
                          )}
                        </td>
                        {pending ? (
                          <td className="ps-small ar-note">{r.note || <span className="ps-muted">—</span>}</td>
                        ) : t.key === "1" ? (
                          <td>
                            {r.activation_date ? moment(r.activation_date).format("DD MMM YYYY") : "—"}
                            <div className="ar-badges">
                              {r.direct && <Badge bg="primary">Direct</Badge>}
                              {r.activation_date && r.reviewed_at && moment(r.activation_date).isBefore(moment(r.reviewed_at), "day") && (
                                <Badge bg="warning" text="dark">Back-dated</Badge>
                              )}
                            </div>
                          </td>
                        ) : (
                          <td className="ps-small ar-note">{r.reject_reason}</td>
                        )}
                        {!pending && (
                          <td className="ps-small">
                            {r.reviewed_by || "—"}
                            {r.reviewed_at && <div className="ps-muted">{moment(r.reviewed_at).format("DD MMM YYYY")}</div>}
                          </td>
                        )}
                        {pending && (
                          <td className="text-end">
                            {r.status === 3 ? (
                              <Badge bg="secondary">Processing</Badge>
                            ) : (
                              <div className="ar-actions">
                                <Button size="sm" variant="success" onClick={() => setApproving(r)}>Approve</Button>
                                <Button size="sm" variant="outline-danger" onClick={() => setRejecting(r)}>Reject</Button>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>

              <Pagination
                page={page}
                pages={pages}
                total={total}
                limit={limit}
                onPage={(p) => setPage(p)}
                onLimit={(n) => { setLimit(n); setPage(1); }}
              />
            </div>
          </Tab>
        ))}
      </Tabs>

      <DirectActivateModal
        show={showDirect}
        packages={packages}
        onClose={() => setShowDirect(false)}
        onDone={() => { setShowDirect(false); setTab("1"); setPage(1); changed(); }}
      />
      <ApproveModal request={approving} onClose={() => setApproving(null)} onDone={() => { setApproving(null); changed(); }} />
      <RejectModal request={rejecting} onClose={() => setRejecting(null)} onDone={() => { setRejecting(null); changed(); }} />
      <ProofModal request={viewingProof} onClose={() => setViewingProof(null)} />
    </div>
  );
};

export default ActivationRequests;
